import { UserI } from "~/interfaces";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist, createJSONStorage } from "zustand/middleware";

export type Actions = {
  // Email
  removeEmail: () => void;
  updateEmail: (email: string) => void;

  // First Name
  removeFirstName: () => void;
  updateFirstName: (firstName: string) => void;

  // Last Name
  removeLastName: () => void;
  updateLastName: (lastName: string) => void;

  // isAdmin
  toggleIsAdmin: () => void;
  setIsAdmin: (isAdmin: boolean) => void;

  // likedListings
  removeLikedListing: (likedListing: any) => void;
  addLikedListing: (likedListings: any) => void;
  removeAllLikedListings: () => void;

  // isLoggedIn
  toggleIsLoggedIn: () => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;

  // Role
  removeRole: () => void;
  updateRole: (role: string) => void;

  // geolocation
  removeGeolocation: () => void;
  updateGeolocation: (geolocation: any) => void;

  // Reset all fields
  resetUser: () => void;
};

export type State = {
  user: UserI;
};

const initialUser: UserI = {
  email: "",
  first_name: "",
  geolocation: {
    longitude: "",
    latitude: "",
  },
  is_admin: false,
  is_logged_in: false,
  last_name: "",
  liked_listings: [],
  role: "",
};

export const useUserStore = create<State & Actions>()(
  persist(
    immer((set) => ({
      user: initialUser,
      // Email
      removeEmail: () =>
        set((state) => {
          state.user.email = "";
        }),
      updateEmail: (email) =>
        set((state) => {
          state.user.email = email;
        }),

      // First Name
      removeFirstName: () =>
        set((state) => {
          state.user.first_name = "";
        }),
      updateFirstName: (firstName) =>
        set((state) => {
          state.user.first_name = firstName;
        }),

      // Last Name
      removeLastName: () =>
        set((state) => {
          state.user.last_name = "";
        }),
      updateLastName: (lastName) =>
        set((state) => {
          state.user.last_name = lastName;
        }),

      // isAdmin
      toggleIsAdmin: () =>
        set((state) => {
          state.user.is_admin = !state.user.is_admin;
        }),
      setIsAdmin: (isAdmin) =>
        set((state) => {
          state.user.is_admin = isAdmin;
        }),

      // likedListings
      removeLikedListing: (likedListing) =>
        set((state) => {
          state.user.liked_listings = state.user.liked_listings.filter(
            (stateLikedListing) => stateLikedListing.id !== likedListing.id,
          );
        }),
      removeAllLikedListings: () =>
        set((state) => {
          state.user.liked_listings = [];
        }),
      addLikedListing: (likedListing) =>
        set((state) => {
          state.user.liked_listings = [
            ...state.user.liked_listings,
            likedListing,
          ];
        }),

      // isLoggedIn
      toggleIsLoggedIn: () =>
        set((state) => {
          state.user.is_logged_in = !state.user.is_logged_in;
        }),
      setIsLoggedIn: (isLoggedIn) =>
        set((state) => {
          state.user.is_logged_in = isLoggedIn;
        }),

      // Role
      removeRole: () =>
        set((state) => {
          state.user.role = "";
        }),
      updateRole: (role) =>
        set((state) => {
          state.user.role = role;
        }),

      // geolocation
      removeGeolocation: () =>
        set((state) => {
          state.user.geolocation = initialUser.geolocation;
        }),
      updateGeolocation: (geolocation) =>
        set((state) => {
          state.user.geolocation = {
            ...state.user.geolocation,
            ...geolocation,
          };
        }),

      // Reset all user info to initial state
      resetUser: () =>
        set((state) => {
          state.user = { ...initialUser };
        }),
    })),
    {
      name: "user-store",
      storage: createJSONStorage(() => localStorage),
      skipHydration: true,
    },
  ),
);
